import { SecondarySideBar, SquareIcon } from "@kalyzee/kalyzee-ui";
import { MeetingChatAdapter, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppStoreDispatch } from "../../contexts/appStore/context";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useMeetingContainerContext } from "../../contexts/meetingContainer/context";
import { isExpert } from "../../helpers/roles";
import { useExperimentalFeatures } from "../../hooks/experimental";

export const SideBar = React.memo(({
  chatAdapter,
  highlightRequestsNb,
}: {
  chatAdapter?: MeetingChatAdapter,
  highlightRequestsNb: number,
}) => {
  const { containerAPI } = useMeetingContainerContext();

  const [sessionRole] = useMeetingSelector(containerAPI, "session", session => session?.role);
  const [survey] = useMeetingSelector(containerAPI, "surveys", surveys => surveys?.[0]);

  const expFeatures = useExperimentalFeatures();

  const workBoxMode = useAppStoreSelector(state => state.workBoxMode);
  const workBoxOpen = useAppStoreSelector(state => state.workBoxOpen);
  const dispatch = useAppStoreDispatch();

  const [undreadMessagesNb, setUnreadMessagesNb] = useState(0);

  const surveyWorkBoxCanBeOpened = (
    (sessionRole && isExpert(sessionRole))
    || survey
  );

  useEffect(() => {
    const handle = chatAdapter?.addEventListener("messages", (messages) => {
      setUnreadMessagesNb(messages.filter(m => !m.read).length);
    });
    return () => handle?.flush();
  }, [chatAdapter]);

  useEffect(() => {
    if (workBoxMode === "survey" && workBoxOpen && !surveyWorkBoxCanBeOpened) {
      dispatch("setWorkBoxOpen", false);
    }
  }, [surveyWorkBoxCanBeOpened, workBoxMode, workBoxOpen]);

  const toggleWorkBox = useCallback((mode: "inputs" | "survey" | "highlight" | "chat") => {
    if (workBoxMode === mode && workBoxOpen) {
      dispatch("setWorkBoxOpen", false);
    } else {
      dispatch("setWorkBoxMode", mode);
      dispatch("setWorkBoxOpen", true);
    }
  }, [workBoxMode, workBoxOpen, dispatch]);

  const top: React.ReactNode[] = useMemo(() => ([
    <SquareIcon.Variants.ComputerScreen
      key={"workbox-btn-inputs"}
      label={"Affichage"}
      selected={workBoxMode === "inputs" && workBoxOpen}
      onClick={() => toggleWorkBox("inputs")}
    />,
  ]), [workBoxMode, workBoxOpen, toggleWorkBox]);


  const bottom: React.ReactNode[] = useMemo(() => {
    const _bottom: React.ReactNode[] = [];

    if (expFeatures.survey) {
      _bottom.push(
        <SquareIcon.Variants.Poll
          key={"workbox-btn-survey"}
          label="Sondage"
          selected={workBoxMode === "survey" && workBoxOpen}
          onClick={() => toggleWorkBox("survey")}
          nativeProps={{
            disabled: !survey && !isExpert(sessionRole),
          }}
          notificationCount={survey && !(workBoxMode === "survey" && workBoxOpen) ? 1 : undefined}
        />
      );
    }

    if (isExpert(sessionRole) && expFeatures.board) {
      _bottom.push(
        <SquareIcon.Variants.Intervention
          key={"workbox-btn-highlight"}
          label="Interventions"
          selected={workBoxMode === "highlight" && workBoxOpen}
          onClick={() => toggleWorkBox("highlight")}
          notificationCount={highlightRequestsNb > 0 && !(workBoxMode === "highlight" && workBoxOpen) ? highlightRequestsNb : undefined}
        />
      );
    }

    _bottom.push(
      <SquareIcon.Variants.Chat
        key={"workbox-btn-chat"}
        label="Chat"
        selected={workBoxMode === "chat" && workBoxOpen}
        onClick={() => toggleWorkBox("chat")}
        notificationCount={undreadMessagesNb ? undreadMessagesNb : undefined}
      />
    );

    return _bottom;
  }, [expFeatures.survey, expFeatures.board, sessionRole, workBoxMode, workBoxOpen, undreadMessagesNb, survey, toggleWorkBox, highlightRequestsNb]);

  if (!sessionRole) return null; // impossible

  return (
    <SecondarySideBar
      top={top}
      bottom={bottom}
    />
  );
});
